import Logo from 'assets/footer/logo.svg?react'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks/analytics-tracker'
import {
  CompanyItem,
  CompanyWrapper,
  Footer,
  FooterColumnWrapper,
  FooterContent,
  FooterLogoWrapper,
  FooterSocialButtonsContainer,
  FooterSocialButtonsWrapper,
  FooterWrapper,
  IconWrapper,
  Legal,
  LegalBorder,
  ProductItem,
  ProductText,
  ProductsColumnWrapper,
  ProductsWrapper,
  RowFlexWrapper,
  SocialIconWrapper,
  SocialsWrapper,
  StyledTitle,
  TopFooterWrapper,
} from './styled'

import XIcon from 'assets/footer/x-icon.svg?react'
import { useActiveLinking } from 'components/app-layout/header/navigation/use-active-linking'
import { useWindowSize } from 'hooks'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { useStore } from 'services/store'
import { PrivacyLinks } from '../../app-constants/index'
type FooterComponentProps = {
  extraHeight: number
  isActive: boolean
  isOpened: boolean
}

export const FooterComponent: React.FC<FooterComponentProps> = ({
  extraHeight,
  isActive,
  isOpened,
}) => {
  const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.Footer)
  const { isMobile, isTablet } = useWindowSize()
  const { pathname } = useLocation()

  const [show, setShow] = React.useState(false)
  const { showLegal } = useStore()
  const { nestedNavigationList } = useActiveLinking()

  const handleNavigate = (url: string, external?: boolean) => {
    navigationEvent(url, external)
  }

  const layout = (document as Document).getElementById('app-layout')

  /**
   * IT: Hides the footer on mobile
   * WHEN: The widget modal is open on mobile, the footer is hidden
   */
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const widgetParam = urlParams.get('widget')

    if (widgetParam === 'true' && (isMobile || isTablet)) {
      setShow(false)
    } else {
      setShow(true)
    }
  }, [isMobile, isTablet, showLegal])
  if (!show) {
    return null
  }

  return (
    <FooterWrapper opened={isOpened} height={isActive ? extraHeight : 0}>
      <FooterContent>
        <TopFooterWrapper>
          <FooterColumnWrapper>
            {(isMobile || isTablet) && (
              <ProductsColumnWrapper>
                <StyledTitle type="four">Products</StyledTitle>
                <ProductsWrapper>
                  {nestedNavigationList()[0].data.map(({ icon, name, description, link }) => (
                    <ProductItem
                      onClick={() => link && navigationEvent(link)}
                      key={`${name}-${description}`}
                    >
                      <IconWrapper src={icon as string} />
                      <ProductText type="four">{name}</ProductText>
                    </ProductItem>
                  ))}
                </ProductsWrapper>
              </ProductsColumnWrapper>
            )}
            <CompanyWrapper>
              <StyledTitle type="four">Company</StyledTitle>
              <RowFlexWrapper>
                {nestedNavigationList()[2].data.map(({ name, link }) => (
                  <CompanyItem
                    type="small"
                    key={`${name}-${link}`}
                    onClick={() =>
                      link && handleNavigate(link, name === 'Documentation' || name === 'Blog')
                    }
                  >
                    {name}
                  </CompanyItem>
                ))}
              </RowFlexWrapper>
            </CompanyWrapper>
            <SocialsWrapper>
              <StyledTitle type="four">Socials</StyledTitle>
              <RowFlexWrapper>
                <SocialIconWrapper
                  onClick={() => navigationEvent('https://twitter.com/phuture_finance', true)}
                >
                  <XIcon />
                </SocialIconWrapper>
              </RowFlexWrapper>
            </SocialsWrapper>
          </FooterColumnWrapper>
          {!isMobile && !isTablet && (
            <ProductsColumnWrapper>
              <StyledTitle type="four">Products</StyledTitle>
              <ProductsWrapper>
                {nestedNavigationList()[0].data.map(({ icon, name, description, link }) => (
                  <ProductItem
                    onClick={() =>
                      link && link !== pathname ? navigationEvent(link) : layout?.scrollTo(0, 0)
                    }
                    key={`${name}-${description}`}
                  >
                    <IconWrapper src={icon as string} />
                    <ProductText type="four">{name}</ProductText>
                  </ProductItem>
                ))}
              </ProductsWrapper>
            </ProductsColumnWrapper>
          )}
        </TopFooterWrapper>
        <LegalBorder />
        <Footer>
          <FooterLogoWrapper>
            <Logo />
          </FooterLogoWrapper>
          {!isMobile && (
            <FooterSocialButtonsWrapper>
              {PrivacyLinks.map(({ link, name }) => (
                <FooterSocialButtonsContainer
                  key={`${link}-${name}`}
                  onClick={() => navigationEvent(link, true)}
                >
                  {name}
                </FooterSocialButtonsContainer>
              ))}
            </FooterSocialButtonsWrapper>
          )}
        </Footer>
        <Legal>
          Disclaimer: This content is for informational purposes only and is not legal, tax,
          investment, financial, or other advice. You should not take, or refrain from taking, any
          action based on any information contained herein, or any other information that we make
          available at any time, including blog posts, data, articles, links to third-party content,
          discord content, news feeds, tutorials, tweets, and videos. Before you make any financial,
          legal, technical, or other decisions, you should seek independent professional advice from
          a licensed and qualified individual in the area for which such advice would be
          appropriate. This information is not intended to be comprehensive or address all aspects
          of Phuture or its products. There is additional documentation on Phuture’s website about
          the functioning of Phuture, and its ecosystem and community. You shall not purchase or
          otherwise acquire any of our token products if you are: a citizen, resident (tax or
          otherwise), green card holder, incorporated in, owned or controlled by a person or entity
          in, located in, or have a registered office or principal place of business in the US (a
          &quot;US Person&quot;), or if you are a person in any jurisdiction in which such offer,
          sale, and/or purchase of any of our token products is unlawful, prohibited, or
          unauthorised (together with US Person, a &quot;Restricted Person&quot;). The term
          &quot;Restricted Person&quot; includes, but is not limited to, any natural person residing
          in, or any firm, company, partnership, trust, corporation, entity, government, state or
          agency of a state, or any other incorporated or unincorporated body or association,
          association or partnership (whether or not having separate legal personality) that is
          established and/or lawfully existing under the laws of, a jurisdiction in which such
          offer, sale, and/or purchase of any of our token products is unlawful, prohibited, or
          unauthorised). You shall not resell or otherwise transfer any of our token products to any
          Restricted Person. The transfer or resale of any of our token products to any Restricted
          Person is not permitted. You shall read the Terms of Service and use our Website in
          compliance with the Terms of Service.
        </Legal>
        {isMobile && (
          <FooterSocialButtonsWrapper>
            {PrivacyLinks.map(({ link, name }) => (
              <FooterSocialButtonsContainer
                key={`${link}-${name}`}
                onClick={() => navigationEvent(link, true)}
              >
                {name}
              </FooterSocialButtonsContainer>
            ))}
          </FooterSocialButtonsWrapper>
        )}
      </FooterContent>
    </FooterWrapper>
  )
}
